import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  makeStyles,
  IconButton,
  Box,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { bhpColor } from "styles/globals";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      borderRadius: "8px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    },
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: bhpColor.blueGrey1,
    zIndex: 10,
  },
  dialogContent: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: "400px",
  },
  imageContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#f5f5f5",
    minHeight: "300px",
    flex: 1,
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    "&:hover": {
      opacity: 0.9,
    },
  },
  bottomSection: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${bhpColor.blueGrey4}`,
  },
  proceedButton: {
    backgroundColor: bhpColor.orange1,
    color: "white",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 24px",
    "&:hover": {
      backgroundColor: bhpColor.orange2,
    },
  },
  moreInfoButton: {
    color: bhpColor.orange1,
    borderColor: bhpColor.orange1,
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 24px",
    "&:hover": {
      borderColor: bhpColor.orange2,
      color: bhpColor.orange2,
      backgroundColor: "transparent",
    },
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100px",
  },
}));

interface HubstarDialogueProps {
  isOpen: boolean;
  onClose: () => void;
}

const HUBSTAR_URL = "https://bhp.smartway2book.com.au/se/#/week-view";
const SHAREPOINT_URL =
  "https://spo.bhpbilliton.com/sites/globalproperty/SitePages/HubStar.aspx?csf=1&web=1&e=WLaG7Y&CID=2491be63-478a-4be9-9752-f63809735f10";

export const HubstarDialogue: React.FC<HubstarDialogueProps> = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleImageClick = () => {
    window.open(HUBSTAR_URL, "_blank");
  };

  const handleProceedClick = () => {
    window.open(HUBSTAR_URL, "_blank");
    onClose();
  };

  useEffect(() => {
    async function fetchImageUrl() {
      try {
        const response = await fetch("api/hubstar_banner_images/?name=Dialog");
        const data = await response.json();
        if (response.ok) {
          setImageUrl(data.results[0].picture);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.error("Error loading banner image:", err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchImageUrl();
  }, []);

  const handleMoreInfoClick = () => {
    window.open(SHAREPOINT_URL, "_blank");
  };

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (imageError || !imageUrl) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" className={classes.root}>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.imageContainer} onClick={handleImageClick}>
          <img
            src={imageUrl}
            alt="HubStar Promotional"
            className={classes.image}
            onError={() => setImageError(true)}
          />
        </Box>

        <Box className={classes.bottomSection}>
          <Button
            variant="outlined"
            className={classes.moreInfoButton}
            onClick={handleMoreInfoClick}
          >
            More Info
          </Button>
          <Button
            variant="contained"
            className={classes.proceedButton}
            onClick={handleProceedClick}
          >
            Go to HubStar
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HubstarDialogue;
